import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogOut, ArrowLeft, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const EmployeeHistory = ({ auth, onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/time/employee/${auth.employee.id}?limit=50`);
      setEntries(response.data);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="sticky top-0 z-10 border-b border-white/10 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/employee/dashboard')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <History className="w-5 h-5 text-blue-400" />
        <h1 className="text-white font-semibold">Historique</h1>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="logout-button">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Chargement...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">Aucun historique disponible</p>
          </div>
        ) : (
          entries.map((entry) => (
            <Card
              key={entry.id}
              className="p-4 rounded-xl bg-slate-800/70 border-slate-700/50"
              data-testid={`history-entry-${entry.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white">
                  {entry.date ? format(new Date(entry.date), 'EEEE d MMMM', { locale: fr }) : 'Date inconnue'}
                </h3>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                  entry.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                  entry.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {entry.status === 'completed' ? 'Terminé' : entry.status === 'active' ? 'Actif' : 'En attente'}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Entrée</p>
                  <p className="text-lg font-mono font-bold text-emerald-400">
                    {entry.clock_in ? format(new Date(entry.clock_in), 'HH:mm') : '--:--'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Sortie</p>
                  <p className="text-lg font-mono font-bold text-red-400">
                    {entry.clock_out ? format(new Date(entry.clock_out), 'HH:mm') : '--:--'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total</p>
                  <p className="text-lg font-mono font-bold text-white">
                    {entry.total_hours ? `${entry.total_hours}h` : '--'}
                  </p>
                </div>
              </div>

              {entry.breaks && entry.breaks.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700/50">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                    {entry.breaks.length} pause(s) - {entry.breaks.reduce((s, b) => s + (b.duration_minutes || 0), 0)} min
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {entry.breaks.map((brk, idx) => (
                      <span key={idx} className="text-xs font-mono text-slate-400 bg-slate-700/50 px-2 py-1 rounded">
                        {brk.start_time ? format(new Date(brk.start_time), 'HH:mm') : '--:--'}
                        {' → '}
                        {brk.end_time ? format(new Date(brk.end_time), 'HH:mm') : 'En cours'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default EmployeeHistory;
